import type { ICondition } from "../types/conditions/common.ts";
import type { ValueResolver } from "../types/resolvers.ts";
import type {
  IResolvableBooleanValue,
  IResolvableNumberValue,
  IResolvableObjectValue,
  IResolvableStringValue,
  Obj,
  ResolvableArrayValue,
} from "../types/values.ts";
import { isResolvable } from "../valueResolvers/shared/index.ts";

type TypeByTypeString = {
  boolean: boolean;
  number: number;
  string: string;
  object: Obj;
};

type ResolvableValueByTypeString = {
  boolean: IResolvableBooleanValue;
  number: IResolvableNumberValue;
  string: IResolvableStringValue;
  object: IResolvableObjectValue;
};

export const createThrowConditionNotImplementedForEvaluator =
  (evaluatorName: string) =>
  (condition: ICondition): never => {
    throw new Error(`Condition type "${JSON.stringify(condition)}" not implemented for "${evaluatorName}".`);
  };

export const getSingleValue = <TypeString extends keyof TypeByTypeString>(
  type: TypeString,
  value: TypeByTypeString[TypeString] | ResolvableValueByTypeString[TypeString],
  resolve: ValueResolver,
) => {
  if (type === "object") {
    const result = isResolvable(value) ? resolve(value as ResolvableValueByTypeString[TypeString], resolve) : value;

    if (typeof result !== type) throw new Error(`Value is of type "${typeof result}"; expected type to be "${type}".`);
    if (isResolvable(result)) throw new Error(`Value is of a resolvable type after being resolved.`);

    return result as TypeByTypeString[TypeString];
  }

  const result = typeof value !== type ? resolve(value as ResolvableValueByTypeString[TypeString], resolve) : value;

  if (typeof result !== type) {
    throw new Error(`Value is of type "${typeof result}"; expected type to be "${type}".`);
  }

  return result as TypeByTypeString[TypeString];
};

export const getArrayValues = <TypeString extends keyof TypeByTypeString>(
  type: TypeString,
  subject: Array<TypeByTypeString[TypeString]> | ResolvableArrayValue,
  resolve: ValueResolver,
) =>
  ((Array.isArray(subject)
    ? subject.map((value: TypeByTypeString[TypeString] | ResolvableValueByTypeString[TypeString]) =>
        getSingleValue(type, value, resolve),
      )
    : resolve(subject, resolve)) as Array<TypeByTypeString[TypeString]>) ?? [];
